import { LitElement, html, css, nothing } from "lit";
import type { PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

/** Elements that are natively tabbable. `[tabindex]` matches are filtered by tabIndex >= 0. */
const TABBABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button",
  "input",
  "select",
  "textarea",
  "iframe",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]",
  "[tabindex]",
].join(",");

function isTabbable(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el.hasAttribute("disabled")) return false;
  if (el.getAttribute("aria-hidden") === "true") return false;
  if (el.tabIndex < 0) return false;
  return el.getClientRects().length > 0;
}

/**
 * Walks the *flattened* tree (through slots and into shadow roots) collecting
 * tabbables in tab order. Walking into shadow roots is what makes the trap work
 * at all: a slotted <bn-button> is not itself focusable — the native <button>
 * inside its shadow root is.
 */
function collectTabbable(node: Element, out: HTMLElement[]): void {
  if (node instanceof HTMLSlotElement) {
    for (const assigned of node.assignedElements({ flatten: true })) collectTabbable(assigned, out);
    return;
  }
  if (node.matches(TABBABLE_SELECTOR) && isTabbable(node)) out.push(node);
  if (node.shadowRoot) {
    for (const child of Array.from(node.shadowRoot.children)) collectTabbable(child, out);
    return;
  }
  for (const child of Array.from(node.children)) collectTabbable(child, out);
}

/** The innermost active element, following shadow roots. */
function deepActiveElement(): HTMLElement | null {
  let el: Element | null = document.activeElement;
  while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement;
  return el instanceof HTMLElement ? el : null;
}

/**
 * bn-drawer is the right-side sheet for detail-without-navigation. The audit
 * record detail (`04` §7.2) and the policy quick-view open here rather than
 * pushing a route, so the filtered list the user assembled behind them survives
 * being read. Use bn-modal instead when the user must answer before continuing.
 *
 * It is *controlled* the same way bn-modal is — set `open` and listen for
 * `close` to clear it; the drawer never closes itself behind your back. While
 * open it is a modal dialog: focus is trapped in the sheet, Escape and a scrim
 * click request a close, focus returns to whatever opened it, and the page
 * behind it does not scroll. While closed it is `inert` and out of the tab
 * order, and it releases the page's scroll lock unconditionally — including
 * when it is removed from the DOM while still open, which is the failure that
 * leaves a customer with a page they cannot scroll.
 *
 * Unlike bn-modal it stays in the DOM when closed, so it can slide rather than
 * appear. The slide is `--bino-transition-normal`, which the token layer already
 * collapses to zero under `prefers-reduced-motion`.
 *
 * Properties:
 * - `open` — reflected; the sheet is inert and hidden when false.
 * - `heading` — the sheet's title and accessible name.
 * - `width` — sheet width in px (default 480); never wider than the viewport.
 *
 * Slots:
 * - default — the detail body; it scrolls, the header and footer do not.
 * - `footer` — the action row.
 *
 * Events:
 * - `close` — Escape, the close button, or a scrim click. Composed.
 *
 *   <bn-drawer
 *     ?open=${!!this.record}
 *     heading="Audit record"
 *     .width=${560}
 *     @close=${() => (this.record = undefined)}
 *   >
 *     <bn-kv .rows=${this.recordRows}></bn-kv>
 *     <bn-button slot="footer" @click=${this.explain}>Explain this decision</bn-button>
 *   </bn-drawer>
 */
@customElement("bn-drawer")
export class BnDrawer extends LitElement {
  /** Whether the sheet is shown. Controlled by the owner. */
  @property({ type: Boolean, reflect: true }) open = false;
  /** The sheet's title and accessible name. */
  @property() heading = "";
  /** Sheet width in px. It never exceeds the viewport. */
  @property({ type: Number }) width = 480;

  @state() private hasFooter = false;

  /** What had focus before the drawer opened, so it can be given back. */
  private opener: HTMLElement | null = null;
  /** The page's own `body.style.overflow`, held while the scroll lock is on. */
  private lockedOverflow: string | null = null;

  static override styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: var(--bino-z-modal);
      /* The host covers the viewport even when closed so the sheet can slide
         out; it must never swallow a click meant for the page underneath. */
      pointer-events: none;
      font-family: var(--bino-font-sans);
    }
    .scrim {
      position: absolute;
      inset: 0;
      background: var(--bino-scrim);
      opacity: 0;
      visibility: hidden;
      transition:
        opacity var(--bino-transition-normal),
        visibility var(--bino-transition-normal);
    }
    :host([open]) .scrim {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }
    .sheet {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      max-width: 100%;
      display: flex;
      flex-direction: column;
      background: var(--bino-surface);
      border-left: 1px solid var(--bino-border);
      box-shadow: var(--bino-shadow-page);
      transform: translateX(100%);
      visibility: hidden;
      transition:
        transform var(--bino-transition-normal),
        visibility var(--bino-transition-normal);
    }
    :host([open]) .sheet {
      transform: none;
      visibility: visible;
      pointer-events: auto;
      /* Visibility is deliberately NOT transitioned on the way in. A
         transitioned visibility still computes to "hidden" at progress 0, so the
         sheet is not focusable in the frame it opens and the focus move silently
         does nothing. On the way out it stays transitioned (the rule above), so
         the sheet is still painted while it slides off. */
      transition: transform var(--bino-transition-normal);
    }
    .sheet:focus {
      outline: none;
    }
    .head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--bino-space-md);
      padding: var(--bino-space-md) var(--bino-space-lg);
      border-bottom: 1px solid var(--bino-border);
      flex-shrink: 0;
    }
    .title {
      margin: 0;
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      color: var(--bino-text);
    }
    .close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      background: none;
      border: 1px solid transparent;
      border-radius: var(--bino-radius-sm);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-lg);
      line-height: 1;
      cursor: pointer;
      color: var(--bino-text-secondary);
      transition:
        color var(--bino-transition-fast),
        background var(--bino-transition-fast);
    }
    .close:hover {
      background: var(--bino-surface-hover);
      color: var(--bino-text);
    }
    .close:focus-visible {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    .body {
      flex: 1 1 auto;
      padding: var(--bino-space-lg);
      overflow-y: auto;
      /* Do not chain a finished scroll of the detail body onto the page. */
      overscroll-behavior: contain;
      color: var(--bino-text);
      font-size: var(--bino-font-size-md);
    }
    .footer {
      display: flex;
      justify-content: flex-end;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-md) var(--bino-space-lg);
      border-top: 1px solid var(--bino-border);
      flex-shrink: 0;
    }
    .hidden {
      display: none;
    }
  `;

  private onDocumentKeydown = (e: KeyboardEvent) => {
    if (this.open && e.key === "Escape") {
      e.stopPropagation();
      this.requestClose();
    }
  };

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this.onDocumentKeydown);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.onDocumentKeydown);
    // A drawer torn down while open must not leave the page unscrollable.
    this.unlockScroll();
    this.restoreFocus();
  }

  override updated(changed: PropertyValues<this>) {
    if (!changed.has("open")) return;
    if (this.open) {
      this.opener = deepActiveElement();
      this.lockScroll();
      // Focus the sheet itself rather than its first control: the drawer is a
      // reading surface, and focusing the container gets the whole dialog
      // announced without arming an action.
      this.sheet()?.focus();
    } else {
      this.unlockScroll();
      this.restoreFocus();
    }
  }

  private sheet(): HTMLElement | null {
    return this.renderRoot.querySelector<HTMLElement>(".sheet");
  }

  private restoreFocus() {
    const opener = this.opener;
    this.opener = null;
    if (opener?.isConnected) opener.focus();
  }

  private lockScroll() {
    if (this.lockedOverflow !== null) return;
    this.lockedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }

  private unlockScroll() {
    if (this.lockedOverflow === null) return;
    document.body.style.overflow = this.lockedOverflow;
    this.lockedOverflow = null;
  }

  private requestClose() {
    this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }));
  }

  /** Tab and Shift+Tab wrap inside the sheet instead of reaching the page behind it. */
  private onKeydown = (e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const sheet = this.sheet();
    if (!sheet) return;
    const stops: HTMLElement[] = [];
    collectTabbable(sheet, stops);
    if (stops.length === 0) {
      e.preventDefault();
      return;
    }
    const index = stops.indexOf(deepActiveElement() as HTMLElement);
    if (e.shiftKey) {
      if (index <= 0) {
        e.preventDefault();
        stops[stops.length - 1]?.focus();
      }
      return;
    }
    if (index === -1 || index === stops.length - 1) {
      e.preventDefault();
      stops[0]?.focus();
    }
  };

  private onSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this.hasFooter = slot
      .assignedNodes({ flatten: true })
      .some((n) => n.nodeType === Node.ELEMENT_NODE || (n.textContent ?? "").trim() !== "");
  }

  override render() {
    return html`
      <div class="scrim" @click=${this.requestClose}></div>
      <aside
        class="sheet"
        style="width:min(${this.width}px, 100%)"
        role="dialog"
        aria-modal="true"
        aria-labelledby=${this.heading ? "drawer-title" : nothing}
        aria-label=${this.heading ? nothing : "Details"}
        tabindex="-1"
        ?inert=${!this.open}
        @keydown=${this.onKeydown}
      >
        <div class="head">
          <h2 class="title" id="drawer-title">${this.heading}</h2>
          <button class="close" type="button" title="Close" aria-label="Close" @click=${this.requestClose}>
            &times;
          </button>
        </div>
        <div class="body"><slot></slot></div>
        <div class="footer ${this.hasFooter ? "" : "hidden"}">
          <slot name="footer" @slotchange=${this.onSlotChange}></slot>
        </div>
      </aside>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-drawer": BnDrawer;
  }
}
